import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ConfigProvider, Form } from 'antd';
import { Modal } from 'antd';
import styled from 'styled-components';
import { Button, Checkbox, Colors, Input, Row, Select, Text } from 'basic-ui-lib';
import InputPhone from 'basic-ui-lib/components/Input/InputPhone';
import { useAppDispatch } from 'store/hooks';
import storeActions from 'modules/store/services/actions';
import storeSelectors from 'modules/store/services/selectors';
import { BusinessType, BUSINESS_TYPE_LABELS } from 'modules/store/services/constants';
import { v4 as uuidv4 } from 'uuid';
import storeApis from 'modules/store/services/apis';
import { IResponseData } from 'services/response';
import { IDetailStore } from 'modules/store/interfaces/api';

const BUSINESS_TYPES = Object.values(BusinessType) as BusinessType[];

const RequiredLabel = ({ children }: { children: string }) => (
  <>
    {children} <span style={{ color: Colors.other[3] }}>*</span>
  </>
);

export interface INewStoreFormValues {
  firstName: string;
  lastName: string;
  businessName: string;
  phone: string;
  email?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  timezone?: string;
  businessType?: BusinessType;
}

type NewStoreModalConfigs = {
  storeId?: string;
};

type NewStoreModalRef = {
  open: (configs?: NewStoreModalConfigs) => void;
  close: () => void;
};

type BusinessTypeSelectorProps = {
  value?: BusinessType;
  onChange?: (val: BusinessType | undefined) => void;
};

const BusinessTypeSelector = ({ value, onChange }: BusinessTypeSelectorProps) => (
  <BusinessTypeGrid>
    {BUSINESS_TYPES.map((type) => (
      <BusinessTypeCheckbox
        key={type}
        label={BUSINESS_TYPE_LABELS[type]}
        circle
        value={value === type}
        onChange={() => onChange?.(value === type ? undefined : type)}
      />
    ))}
  </BusinessTypeGrid>
);

export const useNewStoreModalRef = () => useRef<NewStoreModalRef>(null);

type Props = {};

const NewStoreModal = forwardRef<NewStoreModalRef, Props>((_props, ref) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<INewStoreFormValues>();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const configsRef = useRef<NewStoreModalConfigs | null>(null);
  const timezones = storeSelectors.getTimezones();

  const timezoneOptions = timezones.map((tz) => ({
    value: tz.value,
    label: tz.name,
  }));

  const handleOpen = async (configs?: NewStoreModalConfigs) => {
    configsRef.current = configs || null;
    dispatch(storeActions.getListTimezones.fetch());
    setIsOpen(true);

    if (configs?.storeId) {
      try {
        const res: IResponseData<IDetailStore> = await storeApis.detailStore(configs.storeId);

        if (res?.data?.data) {
          const storeDetail = res.data.data;
          form.setFieldsValue({
            firstName: storeDetail.firstName,
            lastName: storeDetail.lastName,
            businessName: storeDetail.businessName,
            phone: storeDetail.phone,
            email: storeDetail.email,
            street: storeDetail.address,
            city: storeDetail.city,
            state: storeDetail.state,
            zip: storeDetail.zip,
            timezone: storeDetail.timezone,
            businessType: storeDetail.businessType,
          });
        }
      } catch (error) {
        console.error('Error fetching store details:', error);
      }
    }
  };

  const handleClose = () => {
    form.resetFields();
    setIsOpen(false);
    configsRef.current = null;
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      dispatch(
        storeActions.createStore.fetch({
          storeId: uuidv4(),
          firstName: values.firstName,
          lastName: values.lastName,
          businessName: values.businessName,
          email: values.email,
          phone: values.phone,
          address: values.street,
          city: values.city,
          state: values.state,
          zip: values.zip,
          timezone: values.timezone,
          businessType: values.businessType,
        })
      );

      handleClose();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  return (
    <StyledModal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      closable={false}
      width={1048}
      centered
      destroyOnClose
    >
      <ModalContent>
        {/* Header */}
        <ModalHeader>
          <Text title_h5 semibold color='neutral.text.5'>
            {configsRef.current?.storeId ? 'Edit Store' : 'New Store'}
          </Text>
        </ModalHeader>

        {/* Form Content */}
        <FormContainer>
          <ConfigProvider
            theme={{
              components: {
                Form: {
                  labelFontSize: 14,
                  labelColor: Colors.neutral.text[5],
                  verticalLabelPadding: '0 0 4px',
                },
              },
            }}
          >
            <Form form={form} layout='vertical' requiredMark={false}>
              {/* Row 1: First Name & Last Name */}
              <Row gap={16} alignItems='flex-start' className='form-row'>
                <FormCol>
                  <Form.Item
                    name='firstName'
                    label={<RequiredLabel>First Name</RequiredLabel>}
                    rules={[
                      { required: true, message: 'First name is required' },
                    ]}
                  >
                    <Input placeholder='Please enter...' />
                  </Form.Item>
                </FormCol>
                <FormCol>
                  <Form.Item
                    name='lastName'
                    label={<RequiredLabel>Last Name</RequiredLabel>}
                    rules={[
                      { required: true, message: 'Last name is required' },
                    ]}
                  >
                    <Input placeholder='Please enter...' />
                  </Form.Item>
                </FormCol>
              </Row>

              {/* Row 2: Business Name & Phone */}
              <Row gap={16} alignItems='flex-start' className='form-row'>
                <FormCol>
                  <Form.Item
                    name='businessName'
                    label={<RequiredLabel>Business Name</RequiredLabel>}
                    rules={[
                      { required: true, message: 'Business name is required' },
                    ]}
                  >
                    <Input placeholder='Please enter...' />
                  </Form.Item>
                </FormCol>
                <FormCol>
                  <Form.Item
                    name='phone'
                    label={<RequiredLabel>Phone</RequiredLabel>}
                    rules={[{ required: true, message: 'Phone is required' }]}
                  >
                    <InputPhone placeholder='(000) 000-0000' />
                  </Form.Item>
                </FormCol>
              </Row>

              {/* Row 3: Email & Street */}
              <Row gap={16} alignItems='flex-start' className='form-row'>
                <FormCol>
                  <Form.Item name='email' label='Email'>
                    <Input placeholder='Please enter...' />
                  </Form.Item>
                </FormCol>
                <FormCol>
                  <Form.Item name='street' label='Street'>
                    <Input placeholder='Please enter...' />
                  </Form.Item>
                </FormCol>
              </Row>

              {/* Row 4: City & State */}
              <Row gap={16} alignItems='flex-start' className='form-row'>
                <FormCol>
                  <Form.Item name='city' label='City'>
                    <Input placeholder='Please enter...' />
                  </Form.Item>
                </FormCol>
                <FormCol>
                  <Form.Item name='state' label='State'>
                    <Input placeholder='Please enter...' />
                  </Form.Item>
                </FormCol>
              </Row>

              {/* Row 5: Zip Code & Timezone */}
              <Row gap={16} alignItems='flex-start' className='form-row'>
                <FormCol>
                  <Form.Item name='zip' label='Zip Code'>
                    <Input placeholder='Please enter...' />
                  </Form.Item>
                </FormCol>
                <FormCol>
                  <Form.Item name='timezone' label='Timezone'>
                    <Select
                      options={timezoneOptions}
                      placeholder='Please enter...'
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </FormCol>
              </Row>

              {/* Business Type */}
              <div className='business-type-section'>
                <Text body_1 color='neutral.text.5'>Business Type</Text>
                <Form.Item name='businessType' noStyle>
                  <BusinessTypeSelector />
                </Form.Item>
              </div>
            </Form>
          </ConfigProvider>
        </FormContainer>

        {/* Footer */}
        <ModalFooter>
          <Button type_cancel lg style={{ flex: 1 }} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type_second
            lg
            style={{ flex: 1 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            OK
          </Button>
        </ModalFooter>
      </ModalContent>
    </StyledModal>
  );
});

NewStoreModal.displayName = 'NewStoreModal';

export default NewStoreModal;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
    border-radius: 4px;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  background: ${Colors.neutral.bg[1]};
`;

const ModalHeader = styled.div`
  padding: 24px;
`;

const FormContainer = styled.div`
  padding: 0 24px 24px 24px;
  display: flex;
  flex-direction: column;

  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item-label > label {
    font-family: Poppins;
    font-weight: 400;
  }

  .ant-form-item-explain-error {
    font-family: Poppins;
    font-size: 12px;
    text-align: right;
  }

  .business-type-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const FormCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ModalFooter = styled.div`
  display: flex;
  padding: 16px;
  gap: 16px;
  box-shadow: 0px -4px 8px 0px rgba(0, 0, 0, 0.08);
`;

const BusinessTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  row-gap: 14px;
`;

const BusinessTypeCheckbox = styled(Checkbox)`
  padding-right: 8px;
  align-items: center;

  .styled-text {
    font-size: 14px;
    font-weight: 400;
    white-space: nowrap;
  }
`;
